import classcat from "classcat";
import React, { Ref } from "react";

import { Checkbox } from "@touchtech/baselayer-ui";
import {
  IconArrowDown,
  SearchInput,
  Spinner,
} from "@touchtech/web-baselayer-ui";

import CollectionStyleItem from "@is/components/CollectionStyleItem";
import { Collection, CollectionStylesResponse } from "@is/types";

import css from "./SearchStoreFavorites.module.css";
import useManageSearchStoreFavorites from "./hooks/useManageSearchStoreFavorites";

type Props = {
  onStylesGet: (
    term: string,
    includeOutOfStockItems: boolean,
    page?: number
  ) => Promise<CollectionStylesResponse>;
  onStyleToCollectionAdd: (
    styleId: string,
    variantId: string
  ) => Promise<Collection>;
  onSearchFocus?: () => void;
  isPending?: boolean;
  searchRef?: Ref<HTMLInputElement>;
  searchContentRef?: Ref<HTMLDivElement>;
  includedVariants: string[];
};

const SearchStoreFavorites = ({
  onStylesGet,
  onStyleToCollectionAdd,
  onSearchFocus,
  isPending = false,
  searchRef,
  searchContentRef,
  includedVariants,
}: Props) => {
  const {
    t,
    value,
    handleShowOnlyInStockChange,
    showOnlyInStock,
    handleChange,
    handleReset,
    handleShowNextPage,
    styles,
    showShowMoreButton,
    handleInputOnFocus,
    pending,
  } = useManageSearchStoreFavorites({
    onStylesGet,
    onSearchFocus,
    includedVariants,
    isPending,
  });

  return (
    <div className={css.container}>
      <div className={css.titleContainer}>
        <h3 className={classcat([css.title, "text-headline-3xl"])}>
          {t("Search and add")}
        </h3>
        <div className={css.checkboxContainer}>
          <Checkbox
            checked={showOnlyInStock}
            onChange={handleShowOnlyInStockChange}
          />
          <span className={classcat([css.showInStock, "text-label-lg"])}>
            {t("Show only in-stock items")}
          </span>
        </div>
      </div>
      <div className={css.inputContainer}>
        <SearchInput
          value={value}
          placeholder={t("Type style name or ID to search")}
          sizing="lg"
          width="full"
          onChange={handleChange}
          onReset={handleReset}
          onFocus={handleInputOnFocus}
          innerRef={searchRef}
        />
      </div>
      <div className={css.searchArea} ref={searchContentRef}>
        {pending && (
          <div className={css.spinnerContainer}>
            <Spinner className="text-headline-5xl" />
          </div>
        )}

        {styles.length ? (
          <div className={css.searchResults}>
            {styles.map((style, index) => (
              <CollectionStyleItem
                key={`${style.styleId}-${style.variantId}`}
                collectionStyle={style}
                index={index}
                onAdd={onStyleToCollectionAdd}
              />
            ))}
          </div>
        ) : null}

        {showShowMoreButton && (
          <button
            className={classcat(["text-label-xl", css.showMore])}
            onClick={handleShowNextPage}
          >
            <IconArrowDown />
            {t("Show more")}
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchStoreFavorites;
